import React from 'react';
import { Icon } from '@iconify/react';

/**
 * ProjectCard - Mind Palace 風格的專案卡片
 * 根據不同狀態顯示不同樣式：
 * - Thoughts (純文字/Step1)
 * - Links (Step2/有來源)
 * - Images (圖卡已生成)
 * - Videos (Script/影片)
 */

// 基礎卡片容器
function CardContainer({
  title,
  subtitle,
  children,
  onClick,
  className = '',
  titleClassName = '',
  menuSlot
}) {
  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-[32px] p-8 relative h-[400px] flex flex-col
        shadow-[0_4px_24px_rgba(0,0,0,0.02)]
        hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]
        transition-all duration-300 group overflow-hidden cursor-pointer
        hover:-translate-y-1
        ${className}
      `}
    >
      {/* Menu Slot (右上角) */}
      {menuSlot && (
        <div className="absolute top-4 right-4 z-30">
          {menuSlot}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-0.5 mb-6 relative z-10">
        <span className="text-gray-400 text-xs font-semibold tracking-wide uppercase">
          {subtitle}
        </span>
        <h2 className={`text-2xl font-bold text-gray-900 tracking-tight leading-tight ${titleClassName}`}>
          {title}
        </h2>
      </div>

      {/* Content Area */}
      <div className="flex-1 relative z-0">
        {children}
      </div>

      {/* Floating Action Button - 繼續編輯 */}
      <button
        className="
          absolute bottom-6 right-6 px-4 py-2
          bg-transparent text-gray-400 text-sm font-medium
          rounded-full transition-all z-20
          hover:text-gray-600 hover:bg-gray-100
          active:scale-95
        "
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
      >
        繼續編輯
      </button>
    </div>
  );
}

// Thoughts Card - 純文字/Step1
export function ThoughtsCard({ project, onClick, menuSlot }) {
  const text = project.topic || project.content || project.name || '新專案';

  return (
    <CardContainer
      title="Thoughts"
      subtitle="Your inner"
      onClick={onClick}
      menuSlot={menuSlot}
    >
      <div className="relative h-full overflow-hidden pt-2">
        <p className="text-2xl text-gray-400 font-serif leading-relaxed opacity-90 font-light">
          {text}
        </p>
        {/* 底部漸層淡出 */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
      </div>
    </CardContainer>
  );
}

// Links Card - Step2/有來源 (文件堆疊風格)
export function LinksCard({ project, onClick, menuSlot }) {
  const title = project.topic || project.name || '內容預覽';

  // 嘗試從多個欄位取得內容
  let content = '';
  if (project.selectedContent) {
    // selectedContent 可能是物件或字串
    if (typeof project.selectedContent === 'string') {
      content = project.selectedContent;
    } else if (project.selectedContent.content) {
      content = project.selectedContent.content;
    } else if (project.selectedContent.text) {
      content = project.selectedContent.text;
    }
  }
  if (!content && project.cachedCarousels && project.cachedCarousels.length > 0) {
    // 從 cachedCarousels 取第一個的內容
    const firstCarousel = project.cachedCarousels[0];
    content = firstCarousel.content || firstCarousel.text || '';
  }
  if (!content) {
    content = project.contentDescription || project.topic || project.keywords?.join(', ') || '';
  }

  // 截取內容作為預覽 (移除空行)
  const contentText = content.replace(/\n\n+/g, '\n').trim() || '尚無內容預覽';

  // Document component - 單一文件 (顯示真實內容)
  const DocumentCard = ({ className, title: docTitle, content: docContent, maxLines = 6, cornerColor = 'from-indigo-300 to-indigo-500', showSignature = true }) => (
    <div className={`absolute bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
      {/* 折角效果 */}
      <div
        className={`absolute top-0 right-0 w-6 h-6 bg-gradient-to-br ${cornerColor}`}
        style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
      />
      {/* 文件內容 */}
      <div className="p-3 h-full flex flex-col">
        {/* 標題 */}
        <p className="text-[10px] font-semibold text-indigo-600 mb-2 truncate pr-4">{docTitle}</p>
        {/* 真實內容預覽 */}
        <div className="flex-1 overflow-hidden">
          <p className="text-[8px] text-gray-500 leading-relaxed line-clamp-[8]">
            {docContent}
          </p>
        </div>
        {/* 簽名區 */}
        {showSignature && (
          <div className="mt-2 pt-1 border-t border-gray-100">
            <svg className="w-6 h-4 text-indigo-300" viewBox="0 0 40 24" fill="none">
              <path d="M2 18 C10 8, 15 20, 20 12 S30 4, 38 14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <CardContainer
      title="Source"
      subtitle="Reference your"
      onClick={onClick}
      menuSlot={menuSlot}
      className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50"
    >
      <div className="relative w-full h-full flex items-center justify-center overflow-visible">
        <div className="relative w-72 h-52">
          {/* Left Document - 左傾斜 */}
          <DocumentCard
            className="w-32 h-44 -rotate-12 left-0 top-2 z-10 hover:z-40 hover:scale-105 transition-all duration-300"
            title={title}
            content={contentText}
            cornerColor="from-blue-200 to-blue-400"
            showSignature={false}
          />

          {/* Center Document - 主文件 */}
          <DocumentCard
            className="w-40 h-52 left-1/2 -translate-x-1/2 top-0 z-30 hover:scale-105 transition-all duration-300"
            title={title}
            content={contentText}
            cornerColor="from-indigo-300 to-indigo-500"
            showSignature={true}
          />

          {/* Right Document - 右傾斜 */}
          <DocumentCard
            className="w-28 h-40 rotate-12 right-0 top-4 z-20 hover:z-40 hover:scale-105 transition-all duration-300"
            title={title}
            content={contentText}
            cornerColor="from-purple-200 to-purple-400"
            showSignature={false}
          />
        </div>
      </div>
    </CardContainer>
  );
}

// Images Card - 圖卡已生成 (3:4 比例)
export function ImagesCard({ project, onClick, menuSlot }) {
  const images = project.generatedImages || [];
  const imageUrls = images.map(img => img.imageUrl || img.url || img);

  // 填充預設圖片
  while (imageUrls.length < 3) {
    imageUrls.push(`https://picsum.photos/seed/${Date.now() + imageUrls.length}/300/400`);
  }

  return (
    <CardContainer
      title={
        <span className="flex items-center gap-2">
          Images
          <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {images.length} 張
          </span>
        </span>
      }
      subtitle="Create your"
      onClick={onClick}
      menuSlot={menuSlot}
    >
      <div className="relative w-full h-full flex items-center justify-center">
        {/* 緊湊堆疊的圖片組 - 無白框 - 大張 */}
        <div className="relative w-56 h-56">
          {/* Back Left - 向右向上 */}
          <div className="absolute top-0 left-4 w-28 h-[150px] rounded-xl overflow-hidden shadow-md transform -rotate-6 z-10 hover:z-30 hover:scale-110 transition-all duration-300">
            <img src={imageUrls[1]} alt="Saved 1" className="w-full h-full object-cover" />
          </div>

          {/* Back Right - 向左下移 */}
          <div className="absolute top-6 right-2 w-28 h-[150px] rounded-xl overflow-hidden shadow-md transform rotate-6 z-20 hover:z-30 hover:scale-110 transition-all duration-300">
            <img src={imageUrls[2]} alt="Saved 2" className="w-full h-full object-cover" />
          </div>

          {/* Front Center (Main) */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-[170px] rounded-xl overflow-hidden shadow-lg z-30 hover:scale-105 transition-all duration-300">
            <img src={imageUrls[0]} alt="Saved Main" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </CardContainer>
  );
}

// Videos Card - Script/影片
export function VideosCard({ project, onClick, menuSlot }) {
  const thumbnailUrl = project.thumbnailUrl || project.previewImage ||
    'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=300&fit=crop';
  const title = project.topic || project.name || 'Video Script';

  return (
    <CardContainer
      title={`Videos &\nSounds`}
      subtitle="Catalog your"
      onClick={onClick}
      menuSlot={menuSlot}
      titleClassName="whitespace-pre-line"
    >
      <div className="relative w-full h-full mt-2 group/video">
        {/* Background Element 1: Gradient */}
        <div className="absolute top-0 right-[-10px] w-24 h-28 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl transform rotate-6 opacity-90 overflow-hidden border-2 border-white shadow-sm">
          <div className="w-full h-full bg-gradient-to-br from-orange-400/60 to-red-500/60" />
        </div>

        {/* Background Element 2 */}
        <div className="absolute bottom-0 right-[-20px] w-28 h-36 rounded-tl-[40px] overflow-hidden transform rotate-[-5deg] border-4 border-white shadow-sm z-10 bg-gradient-to-br from-purple-400 to-pink-500">
          <div className="w-full h-full opacity-60" />
        </div>

        {/* Main Element: Video Thumbnail */}
        <div className="absolute bottom-6 left-[-10px] w-44 h-28 bg-blue-600 rounded-3xl overflow-hidden shadow-xl transform rotate-[-8deg] border-[5px] border-white z-20 transition-transform duration-300 group-hover/video:scale-105 group-hover/video:rotate-[-5deg] group-hover/video:z-30">
          <img
            src={thumbnailUrl}
            alt="Video Thumbnail"
            className="w-full h-full object-cover"
          />
          {/* Play Button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Icon icon="mdi:play" className="text-white ml-0.5" width={16} />
            </div>
          </div>
        </div>

        {/* Title Badge */}
        <div className="absolute top-2 left-2 bg-gray-900/80 text-white text-xs px-2 py-1 rounded-full z-40 max-w-[120px] truncate">
          {title}
        </div>
      </div>
    </CardContainer>
  );
}

/**
 * MindPalaceProjectCard - 自動選擇適合的卡片樣式
 * 使用 MindPalace 命名避免與舊 ProjectCard 衝突
 */
export function MindPalaceProjectCard({ project, onClick, menuSlot }) {
  const { currentStep, generatedImages, type } = project;

  // 判斷使用哪種卡片樣式
  const hasImages = generatedImages && generatedImages.length > 0;
  const isScript = type === 'creator-to-script' || type === 'video-script';
  const isStep2 = currentStep === 2;

  if (isScript) {
    return <VideosCard project={project} onClick={onClick} menuSlot={menuSlot} />;
  }

  if (hasImages) {
    return <ImagesCard project={project} onClick={onClick} menuSlot={menuSlot} />;
  }

  if (isStep2) {
    return <LinksCard project={project} onClick={onClick} menuSlot={menuSlot} />;
  }

  // 預設用 Thoughts (純文字/Step1)
  return <ThoughtsCard project={project} onClick={onClick} menuSlot={menuSlot} />;
}

export default MindPalaceProjectCard;
